import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  InputLabel,
} from '@mui/material';
import { FormikProps, Form, Formik, ErrorMessage } from 'formik';
import { ShareFormValues, shareValidationSchema } from './constants.ts';

interface ShareFormProps {
  setIsOpen: (value: boolean) => void;
  selectedPropertyId: string;
}

export const ShareForm = ({
  setIsOpen,
  selectedPropertyId,
}: ShareFormProps) => {
  const labelStyle = {
    color: 'white',
    fontSize: '12px',
    marginBottom: '4px',
  };

  const defaultShareInitialValues: ShareFormValues = {
    email: '',
    phone: '',
    note: selectedPropertyId
      ? `Check this out, found on Immersive Home Designs - ${window.location.origin}/${selectedPropertyId}`
      : '',
  };

  const buildAndOpenEmailAndText = (
    email: string | undefined,
    phone: string | undefined,
    note: string | undefined
  ) => {
    if (email) {
      window.open(
        `mailto:${email}?subject=Check Out This Lot!&body=${note}`,
        '_blank'
      );
    }
    if (phone) {
      window.open(`sms:${phone};?&body=${note}`, '_blank');
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(
      `${window.location.origin}/${selectedPropertyId}`
    );
    setIsOpen(false);
  };

  return (
    <>
      <Typography
        sx={{
          marginBottom: '12px',
          textAlign: 'center',
          display: { xs: 'initial', md: 'none' },
        }}
      >
        Share via Email, phone or both
      </Typography>
      <Typography
        sx={{
          marginBottom: '12px',
          textAlign: 'center',
          display: { xs: 'none', md: 'initial' },
        }}
      >
        Share via Email
      </Typography>
      <Formik
        initialValues={defaultShareInitialValues}
        validationSchema={shareValidationSchema}
        onSubmit={() => {}}
      >
        {({
          values,
          touched,
          errors,
          isValid,
          dirty,
          handleChange,
        }: FormikProps<ShareFormValues>) => (
          <Form>
            <Box className="field">
              {values.email && Boolean(errors.email) && (
                <ErrorMessage
                  name="email"
                  component="div"
                  render={(msg) => (
                    <div className="form-error-message">{msg}</div>
                  )}
                />
              )}
              <InputLabel sx={labelStyle}>Recipient Email Address</InputLabel>
              <TextField
                data-testid="share-form-email-input"
                fullWidth
                className="text-input"
                id="email"
                name="email"
                placeholder="Email"
                onChange={handleChange}
                value={values.email}
                error={touched.email && Boolean(errors.email)}
              />
            </Box>
            <Box
              className="field"
              sx={{ display: { xs: 'initial', md: 'none' } }}
            >
              {values.phone && Boolean(errors.phone) && (
                <ErrorMessage
                  name="phone"
                  component="div"
                  render={(msg) => (
                    <div className="form-error-message">{msg}</div>
                  )}
                />
              )}
              <InputLabel sx={labelStyle}>
                (Optional) Recipient Phone Number
              </InputLabel>
              <TextField
                data-testid="share-form-phone-input"
                fullWidth
                className="text-input"
                id="phone"
                name="phone"
                placeholder="Recipient Phone Number"
                type="phone"
                onChange={handleChange}
                value={values.phone}
                error={touched.phone && Boolean(errors.phone)}
              />
            </Box>

            <Box className="field">
              <InputLabel sx={labelStyle}>(Optional) Note</InputLabel>
              <TextField
                data-testid="share-form-note-input"
                fullWidth
                multiline
                sx={{ backgroundColor: 'white', borderRadius: '4px' }}
                className="text-input"
                id="note"
                name="note"
                placeholder="Note"
                type="note"
                onChange={handleChange}
                value={values.note}
                error={touched.note && Boolean(errors.note)}
                minRows={5}
                maxRows={5}
              />
            </Box>
            <Button
              data-testid="share-modal-submit-button"
              disabled={!isValid || !dirty}
              onClick={() => {
                buildAndOpenEmailAndText(
                  values.email,
                  values.phone,
                  values.note
                );
              }}
              variant="outlined"
              sx={{
                backgroundColor: 'white',
                minWidth: '100%',
                '&:hover': {
                  backgroundColor: '#2095e5',
                  color: 'white',
                },
              }}
            >
              Share
            </Button>

            <Link
              sx={{
                color: 'white',
                display: 'flex',
                justifyContent: 'center',
                marginTop: '12px',
              }}
              onClick={() => {
                // TODO: Let's use useMutation here instead of an IIFE
                void (async () => {
                  await copyLink();
                })();
              }}
              href="#"
              className="forgot-password"
            >
              Copy a direct link to the listing here{' '}
            </Link>
          </Form>
        )}
      </Formik>
    </>
  );
};
